/**
 * Temporary hand-pinned contract types; replaced by the generated client in GDN-3382 (packet F).
 *
 * Source of truth: `tasks-queues-contract-v1.md` sections 1, 5, 6 and 7, plus
 * ADR-0153 on the server side. Nothing here is generated, so every field name
 * below is the wire name the server promises. Packets C (GDN-3379, Home),
 * D (GDN-3380, the verbs) and E (GDN-3381, Claims queues) all read this file;
 * a change to it is a change to the contract and belongs in the contract
 * document first.
 */

import type {
  ClaimWorkItem,
  EncounterWorkNoteEditRequest as GeneratedEncounterWorkNoteEditRequest,
  EncounterWorkNoteItem,
} from '@store/generated/billing-v2';

// ---------------------------------------------------------------------------
// Section 1: work-view keys
// ---------------------------------------------------------------------------

/**
 * Views that honour dispositions and are mutually exclusive.
 *
 * `bill_secondary` (GDN-4023, contract gdn-3991 v1 §7): secondary drafts to
 * send, plus secondary-billable primaries whose draft could not be made. The
 * generated `ClaimWorkQueueKey`, `ClaimWorkViewKey` and counts learn the key
 * from smartscribe-server GDN-4022; until that is on api-test the sites that
 * read them do not typecheck, and the PR's `openapi-preflight` block holds
 * CI rather than any cast here (root CLAUDE.md, "Paired Backend PRs"). Packet
 * F (GDN-3382) retires this hand-pinned kit altogether.
 */
export type QueueViewKey = 'submit' | 'review' | 'awaiting_payer' | 'fix' | 'bill_secondary';

/** Views that never honour dispositions and overlap freely. */
export type ListViewKey = 'all_claim_work' | 'payer_in_progress' | 'payment_posted';

export type WorkViewKey = QueueViewKey | ListViewKey;

/**
 * Legacy keys the RPCs still accept and map server-side, so GDN-3322
 * breadcrumbs and saved views keep resolving.
 */
export type WorkViewAliasKey = 'ready_to_submit' | 'needs_correction';

export const QUEUE_VIEW_KEYS: readonly QueueViewKey[] = [
  'submit',
  'review',
  'awaiting_payer',
  'fix',
  'bill_secondary',
] as const;

export const LIST_VIEW_KEYS: readonly ListViewKey[] = [
  'all_claim_work',
  'payer_in_progress',
  'payment_posted',
] as const;

export const WORK_VIEW_KEYS: readonly WorkViewKey[] = [
  ...QUEUE_VIEW_KEYS,
  ...LIST_VIEW_KEYS,
] as const;

export const WORK_VIEW_ALIASES: Record<WorkViewAliasKey, QueueViewKey> = {
  needs_correction: 'fix',
  ready_to_submit: 'submit',
};

export const WORK_VIEW_LABELS: Record<WorkViewKey, string> = {
  all_claim_work: 'All claim work',
  awaiting_payer: 'Awaiting payer',
  bill_secondary: 'Bill secondary',
  fix: 'Claims to fix',
  payer_in_progress: 'With payer',
  payment_posted: 'Paid',
  review: 'Claims to review',
  submit: 'Claims to submit',
};

/**
 * Rail colors, carried over from the shipped values. `bill_secondary` is new
 * (GDN-4023): purple 700, the one hue no other view wears, so the fifth card
 * and rail tick read as a fifth queue rather than as a shade of Awaiting
 * payer or Paid.
 */
export const WORK_VIEW_RAIL_COLORS: Record<WorkViewKey, string> = {
  all_claim_work: '#64748B',
  awaiting_payer: '#3F51B5',
  bill_secondary: '#7B1FA2',
  fix: '#D84315',
  payer_in_progress: '#3F51B5',
  payment_posted: '#00897B',
  review: '#D97706',
  submit: '#2E7D32',
};

export function isQueueViewKey(key: string): key is QueueViewKey {
  return (QUEUE_VIEW_KEYS as readonly string[]).includes(key);
}

/**
 * Resolve an alias to its canonical key; canonical keys pass through.
 *
 * `Object.hasOwn`, not `in`: this is fed straight from a query string in the
 * MSW handlers, and `'constructor' in WORK_VIEW_ALIASES` is true.
 */
export function resolveWorkViewKey(key: WorkViewKey | WorkViewAliasKey): WorkViewKey {
  return Object.hasOwn(WORK_VIEW_ALIASES, key)
    ? WORK_VIEW_ALIASES[key as WorkViewAliasKey]
    : (key as WorkViewKey);
}

// ---------------------------------------------------------------------------
// Section 5: row additions on every `get_claim_work_page` item
// ---------------------------------------------------------------------------

export type EncounterWorkState = 'active' | 'snoozed' | 'archived';

/** Why a row with a non-active disposition is nonetheless visible. */
export type WorkReturnedReason = 'lifecycle_event' | 'new_claim' | 'snooze_expired';

export interface EncounterWorkDisposition {
  state: EncounterWorkState;
  hidden_until: string | null;
  /**
   * The principal pair comes straight out of the view; `actor_name` is
   * resolved afterwards in the Python page repository and is null whenever
   * that resolution finds nothing (contract v1.1). Every caption that names
   * the actor therefore has to survive the name being absent — see
   * `formatDispositionCaption`, which drops the name rather than printing
   * "Archived by null" or inventing "someone".
   */
  actor_principal_id: string | null;
  actor_principal_kind: string | null;
  actor_name: string | null;
  updated_at: string;
  version: number;
}

/**
 * A work note as the wire carries it: the generated item (contract v1.5 —
 * `updated_at`, `edited_at`, `editable_until`, `author_principal_id`),
 * narrowed to what the row always carries. The schema marks `created_at` and
 * the author fields optional; the server never omits them, and the card reads
 * them without a guard.
 *
 * `updated_at` is a concurrency token, not a time: an edit echoes it back as
 * `expected_updated_at` VERBATIM. Reformatting it through `Date` drops the
 * microseconds and reads as stale.
 */
export type EncounterWorkNote = EncounterWorkNoteItem & {
  author_name: string | null;
  /** Who wrote it, as the server's principal id — the signed-in `user_id` for a person. */
  author_principal_id: string | null;
  claim_id: string | null;
  created_at: string;
  /** When the body was last changed; null on a note nobody has edited. */
  edited_at: string | null;
};

/** The `latest_note` summary carried on a row: no id, no claim id. */
export interface LatestWorkNote {
  body: string;
  /** Same resolution story as the disposition's actor (contract v1.1). */
  author_principal_id: string | null;
  author_name: string | null;
  created_at: string;
}

export interface LastPayerStatus {
  kind: string;
  code: string | null;
  occurred_at: string;
}

export interface OutcomeReason {
  codes: string[];
  category: string | null;
}

/**
 * The blocker codes contract gdn-3991 v1 §2 names on `secondary_blockers`.
 * Copy for each lives in `utils/secondaryBillingPresentation.ts`.
 */
export type SecondaryBlockerCode =
  | 'payer_unresolved'
  | 'policy_order_disputed'
  | 'primary_open_fix'
  | 'primary_claim_number_missing';

/**
 * The fields section 5 adds to every claim work row. Spread onto the generated
 * `ClaimWorkItem` by consumers until packet F regenerates it.
 */
export interface ClaimWorkRowAdditions {
  queue: QueueViewKey | null;
  hidden: boolean;
  returned_reason: WorkReturnedReason | null;
  work_disposition: EncounterWorkDisposition | null;
  work_notes_count: number;
  latest_note: LatestWorkNote | null;
  days_silent: number | null;
  last_payer_status: LastPayerStatus | null;
  outcome_reason: OutcomeReason | null;
  /**
   * `patient_control_number`. Null before submission (v1.1), so a surface that
   * shows Claim # falls back to a short claim id rather than a blank cell.
   */
  claim_number: string | null;
  /**
   * The payer ICN: `claims.payer_claim_control_number`, the PAYER's own number
   * for this claim and the one its phone system asks for. Not `claim_number`,
   * which is ours (v1.4, contract section 5).
   *
   * Null until the claim is adjudicated, and null for good on a claim the
   * clearinghouse rejected, which never reached the payer at all. The Fix
   * column says "No payer claim #" for both and names the clearinghouse only
   * on a rejection.
   */
  payer_claim_control_number: string | null;
  /** The claim's FIRST line's encounter (v1.1). Home needs it for the queue object. */
  encounter_id: string | null;
  /**
   * When the claim went to the payer. Contract section 5 (v1.1). Null on a
   * claim that has never been submitted.
   */
  submitted_at: string | null;
  /**
   * The ERA triage flag the `fix` queue's partially-paid arm reads
   * (`era_triage->>'needs_attention'`). Contract section 5 (v1.1).
   */
  era_needs_attention: boolean;
  /**
   * The Bill secondary queue's fields (GDN-4023, contract gdn-3991 v1 §2).
   * Packet A adds the columns to `claim_work_items`, so every row carries
   * them; they say something on a secondary draft and on a secondary-billable
   * primary, and are false, null or empty everywhere else.
   */
  /** The five-check predicate holds for this row. Only ever true on a primary. */
  secondary_billable: boolean;
  /** Patient responsibility the primary's driving 835 left, in cents. */
  secondary_billable_cents: number | null;
  /** On a primary: its live derived secondary, in any status. Null on a secondary draft. */
  secondary_claim_id: string | null;
  secondary_claim_status: string | null;
  /**
   * On a primary: the order-2 coverage's payer display name; on a secondary
   * draft: its own payer's. Null when the coverage names no payer, which the
   * `payer_unresolved` blocker says in words.
   */
  secondary_payer_name: string | null;
  /**
   * Why the secondary cannot go yet, as the server's codes
   * (`SecondaryBlockerCode`). Typed as strings on purpose: a code the copy
   * has not caught up with still renders, as itself, rather than failing
   * the row.
   */
  secondary_blockers: string[];
  /** `occurred_at` of the primary's driving 835; a secondary draft copies its primary's. */
  primary_era_at: string | null;
}

/**
 * Counts response additions: all EIGHT work-view keys plus the hidden total.
 *
 * Not `Partial`. The contract says the response carries a count for every key,
 * and a missing key at a surface reads as "unknown" rather than as "none" —
 * which is the difference between a badge that is absent and a badge that says
 * 0. The two aliases are request-only and never appear here.
 */
export interface ClaimWorkCountsAdditions {
  work_view_counts: Record<WorkViewKey, number>;
  hidden_count: number;
}

/**
 * `get_claim_work_page`'s response as the contract leaves it.
 *
 * `hidden_count` rides on `counts` HERE, inside the page response, rather
 * than behind a call of its own: builder B put it on the `ClaimWorkCounts`
 * model and the page RPC returns that model inline, so a surface that needs
 * the hidden total already has it in the read it just made. Packet F's
 * generated client picks this shape up and this type goes.
 */
export interface ClaimWorkPageResponse {
  counts: ClaimWorkCountsAdditions & { computed_at: string; matching_count?: number };
  items?: Array<ClaimWorkItem & ClaimWorkRowAdditions>;
  next_cursor?: string | null;
}

// ---------------------------------------------------------------------------
// Section 6: endpoints
// ---------------------------------------------------------------------------

export type EncounterWorkAction = 'archive' | 'snooze' | 'restore';

/** `POST /v2/provider/billing/v2/encounters/{encounter_id}/work-disposition` */
export interface EncounterWorkDispositionRequest {
  action: EncounterWorkAction;
  /** Snooze only, `YYYY-MM-DD`. The server resolves 09:00 in the org timezone. */
  until_date?: string;
  /** Archive or snooze; becomes an `encounter_work_notes` row. */
  note?: string;
  /** The claim on screen, or null when the caller has no claim in hand. */
  claim_id?: string | null;
  /** Optional optimistic-concurrency guard; the server 409s on a mismatch. */
  expected_version?: number;
}

export interface EncounterWorkDispositionResult extends EncounterWorkDisposition {
  encounter_id: string;
  claim_id: string | null;
}

export interface EncounterWorkDispositionResponse {
  disposition: EncounterWorkDispositionResult;
  note: EncounterWorkNote | null;
}

/** `POST /v2/provider/billing/v2/encounters/{encounter_id}/work-notes` */
export interface EncounterWorkNoteRequest {
  body: string;
  claim_id?: string | null;
}

export interface EncounterWorkNoteResponse {
  note: EncounterWorkNote;
}

/** `PATCH /v2/provider/billing/v2/encounters/{encounter_id}/work-notes/{note_id}` (v1.5). */
export type EncounterWorkNoteEditRequest = GeneratedEncounterWorkNoteEditRequest;

/**
 * The refusals an edit or delete can meet (v1.5). `note_not_editable` is a
 * 403 — the window has closed, or the note is someone else's; `stale_note` is
 * a 409 — the note was edited elsewhere since this client read it;
 * `note_not_found` is a 404 — the note is gone, or is not on this encounter.
 * A blank body or a malformed `expected_updated_at` is a 422, as on POST, and
 * is refused client-side before it can travel. `DELETE` answers 204 with no
 * body on success.
 */
export type EncounterWorkNoteErrorCode = 'note_not_editable' | 'stale_note' | 'note_not_found';

export interface EncounterWorkNoteError {
  error_code: EncounterWorkNoteErrorCode;
}

/** `GET /v2/provider/billing/v2/encounters/{encounter_id}/work-notes`, newest first. */
export interface EncounterWorkNotesResponse {
  notes: EncounterWorkNote[];
  disposition: EncounterWorkDisposition | null;
}

/** 409 body: `work_disposition_version_conflict`. */
export interface EncounterWorkDispositionConflict {
  error_code: 'work_disposition_version_conflict';
  disposition: EncounterWorkDispositionResult;
}

// ---------------------------------------------------------------------------
// Section 7: the FE hook
// ---------------------------------------------------------------------------

/** Where the verb was pressed. Flows through to Statsig unchanged. */
export type EncounterWorkSource = 'home' | 'claims_worklist' | 'claim_workbench';

/** The newest verb still in flight (null when none is), for disabling the menu item that fired it. */
export type EncounterWorkPending =
  | 'archive'
  | 'snooze'
  | 'restore'
  | 'note'
  | 'edit'
  | 'delete'
  | null;

/**
 * The subset of `ClaimActionOutcome` this hook reports. The queue advance rule
 * (`features/claim-workbench/logic/queueAdvance.ts`) owns the full union;
 * `archived` and `snoozed` resolve the claim's place, `restored` does not.
 */
export type EncounterWorkOutcome = 'archived' | 'snoozed' | 'restored';

export interface UseEncounterWorkDispositionArgs {
  encounterId: string;
  claimId?: string | null;
  source: EncounterWorkSource;
  /**
   * Called after a successful verb, with what happened. The workbench feeds it
   * to the advance rule; Home and Claims can ignore it.
   */
  onOutcome?: (outcome: EncounterWorkOutcome) => void;
  /**
   * The `version` of the disposition this surface last read, sent as
   * `expected_version` so the server can 409 rather than silently overwrite a
   * colleague who moved the encounter in the meantime. Omit when the surface
   * has no disposition in hand; the server then takes last-writer-wins, which
   * is the correct behaviour for a first archive.
   */
  expectedVersion?: number;
  /**
   * How the archive snackbar names what it just hid, e.g. "Bea Lee · 10 Aug".
   * Optional because only a row surface knows it: the workbench kebab has the
   * patient in the header two inches above the toast, and a snackbar that
   * repeated it would be noise. Absent, the copy drops the name rather than
   * inventing one.
   */
  subjectLabel?: string;
}

export interface EditWorkNoteArgs {
  noteId: string;
  body: string;
  /** The note's `updated_at` as this surface last read it, verbatim — never through `Date`. */
  expectedUpdatedAt: string;
}

export interface DeleteWorkNoteArgs {
  noteId: string;
  /** What Undo re-posts: the body of the note that just left, on the claim it was on. */
  body: string;
  claimId: string | null;
}

export interface UseEncounterWorkDispositionResult {
  archive: (opts?: { note?: string }) => Promise<void>;
  snooze: (opts: { untilDate: string; note?: string }) => Promise<void>;
  restore: () => Promise<void>;
  /**
   * `claimId` overrides the hook's own claim for this one note — Undo uses it
   * to re-post a deleted note on the claim it was on. Absent, the claim on
   * screen travels, as before.
   */
  addNote: (opts: { body: string; claimId?: string | null }) => Promise<void>;
  /** Within the author's window only (v1.5); the server is the authority. */
  editNote: (opts: EditWorkNoteArgs) => Promise<void>;
  deleteNote: (opts: DeleteWorkNoteArgs) => Promise<void>;
  pending: EncounterWorkPending;
}
